/**
 * FINANCIAL DASHBOARD - Overview Page
 * Big metrics, charts, and top spending customers
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MetricCard from '../components/admin/financials/MetricCard';
import CostChart from '../components/admin/financials/CostChart';
import FinancialNav from '../components/admin/financials/FinancialNav';
import { DollarSign, BarChart3, Users, Zap, Calendar, RefreshCw, AlertCircle, TrendingUp, Info } from 'lucide-react';
import apiClient from '../lib/apiClient';

export default function FinancialDashboard() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [tenantFilter, setTenantFilter] = useState('all');

  useEffect(() => {
    loadOverview();
  }, [dateRange, tenantFilter]);

  // Returns YYYY-MM-DD in the browser's local timezone (not UTC)
  const localDateStr = (d = new Date()) => d.toLocaleDateString('en-CA');

  const loadOverview = async () => {
    setLoading(true);
    setError(null);
    try {
      // Calculate date range
      const dates = getDateRange(dateRange);

      const params = new URLSearchParams({
        date_from: dates.from,
        date_to: dates.to,
        today_local: localDateStr()   // tell backend what "today" is in user's timezone
      });

      if (tenantFilter !== 'all') {
        params.append('tenant_id', tenantFilter);
      }

      const response = await apiClient.get(`/admin/financials/overview?${params}`);

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      setOverview(response.data);
      console.log('✅ Financial overview loaded:', response.data);
    } catch (err) {
      console.error('❌ Error loading overview:', err);
      setError(err.message || 'Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (range) => {
    const today = new Date();
    const to = today.toLocaleDateString('en-CA');
    let from;

    switch (range) {
      case '7d': {
        const d = new Date(today); d.setDate(d.getDate() - 7);
        from = d.toLocaleDateString('en-CA');
        break;
      }
      case '30d': {
        const d = new Date(today); d.setDate(d.getDate() - 30);
        from = d.toLocaleDateString('en-CA');
        break;
      }
      case '90d': {
        const d = new Date(today); d.setDate(d.getDate() - 90);
        from = d.toLocaleDateString('en-CA');
        break;
      }
      case 'ytd':
        from = new Date(today.getFullYear(), 0, 1).toLocaleDateString('en-CA');
        break;
      default: {
        const d = new Date(today); d.setDate(d.getDate() - 30);
        from = d.toLocaleDateString('en-CA');
      }
    }

    return { from, to };
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Header with Controls */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              💰 Financial Intelligence
            </h1>
            <p className="text-gray-600 mt-2">Real-time cost tracking, unit economics, and invoicing for all tenants</p>
          </div>
          <button
            onClick={loadOverview}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Quick Filters */}
        <div className="flex gap-4 items-center p-4 bg-white rounded-lg shadow-sm">
          <Calendar className="w-5 h-5 text-gray-400" />
          <div className="flex gap-2">
            <label className="text-sm font-medium text-gray-700">Time Range:</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="text-sm border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="ytd">Year to Date</option>
            </select>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <FinancialNav />

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900">Failed to load financial data</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={loadOverview}
              className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Try again →
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !overview ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Loading financial data...</p>
          <p className="text-sm text-gray-500 mt-1">Calculating costs and metrics</p>
        </div>
      ) : (
        <div className={loading ? 'opacity-50 pointer-events-none' : ''}>

        {/* Big Number Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            label="💵 Today's Spend"
            value={overview?.total_spend_today || 0}
            format="currency"
            icon={<DollarSign className="w-6 h-6 text-blue-600" />}
            isLoading={loading}
          />
          <MetricCard
            label="📊 This Week"
            value={overview?.total_spend_this_week || 0}
            format="currency"
            icon={<BarChart3 className="w-6 h-6 text-green-600" />}
            isLoading={loading}
          />
          <MetricCard
            label="⚡ This Month"
            value={overview?.total_spend_this_month || 0}
            format="currency"
            icon={<Zap className="w-6 h-6 text-yellow-600" />}
            isLoading={loading}
          />
          <MetricCard
            label="📈 Year to Date"
            value={overview?.total_spend_ytd || 0}
            format="currency"
            icon={<TrendingUp className="w-6 h-6 text-purple-600" />}
            isLoading={loading}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Spend by Provider */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">🏢 Spend by Provider</h3>
              <div className="group relative">
                <Info className="w-4 h-4 text-gray-400 cursor-help" />
                <div className="absolute right-0 top-6 w-64 bg-gray-900 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  Breakdown of costs by service provider (Twilio, Anthropic, OpenAI, Supabase)
                </div>
              </div>
            </div>
            {overview && Object.keys(overview?.spend_by_provider || {}).length > 0 ? (
              <CostChart
                type="pie"
                data={Object.entries(overview?.spend_by_provider || {}).map(([name, value]) => ({
                  name: name.charAt(0).toUpperCase() + name.slice(1),
                  value
                }))}
                config={{ nameKey: 'name', valueKey: 'value', height: 300 }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <BarChart3 className="w-12 h-12 mb-3 text-gray-300" />
                <p className="font-medium">No provider data yet</p>
                <p className="text-sm text-center mt-1">Costs will appear after SMS or AI usage</p>
              </div>
            )}
          </div>

          {/* Spend by Event Type */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">📱 Spend by Event Type</h3>
              <div className="group relative">
                <Info className="w-4 h-4 text-gray-400 cursor-help" />
                <div className="absolute right-0 top-6 w-64 bg-gray-900 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  Breakdown by type: SMS inbound/outbound, AI tokens, voice calls, etc.
                </div>
              </div>
            </div>
            {overview && Object.keys(overview?.spend_by_type || {}).length > 0 ? (
              <CostChart
                type="pie"
                data={Object.entries(overview?.spend_by_type || {}).map(([name, value]) => ({
                  name: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                  value
                }))}
                config={{ nameKey: 'name', valueKey: 'value', height: 300 }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <Zap className="w-12 h-12 mb-3 text-gray-300" />
                <p className="font-medium">No event data yet</p>
                <p className="text-sm text-center mt-1">Costs will be categorized by event type</p>
              </div>
            )}
          </div>
        </div>

        {/* Cost Trend */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">📈 Cost Trend Over Time</h3>
            <span className="text-sm text-gray-500">
              {dateRange === '7d' ? 'Last 7 Days' : dateRange === '30d' ? 'Last 30 Days' : dateRange === '90d' ? 'Last 90 Days' : 'Year to Date'}
            </span>
          </div>
          {overview && overview?.cost_trend && overview.cost_trend.length > 0 ? (
            <CostChart
              type="line"
              data={overview.cost_trend}
              config={{ xKey: 'date', yKey: 'cost', height: 300 }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <TrendingUp className="w-12 h-12 mb-3 text-gray-300" />
              <p className="font-medium">No trend data available</p>
              <p className="text-sm text-center mt-1">Cost trends will appear as data accumulates</p>
            </div>
          )}
        </div>

        {/* Unit Economics */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">🎯 Unit Economics</h3>
              <p className="text-sm text-gray-500 mt-1">Key efficiency metrics for your platform</p>
            </div>
            <Link to="/admin/financials/unit-economics" className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
              View Detailed Metrics
              <TrendingUp className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border-2 border-blue-100 bg-blue-50 rounded-lg p-4 hover:border-blue-300 transition-colors">
              <p className="text-xs font-medium text-blue-700 mb-1 flex items-center gap-1">
                💬 Cost per Message
                <span className="group relative">
                  <Info className="w-3 h-3 cursor-help" />
                  <span className="absolute left-0 top-5 w-48 bg-gray-900 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    Average cost for each SMS sent or received
                  </span>
                </span>
              </p>
              <p className="text-2xl font-bold text-gray-900">
                ${parseFloat(overview?.unit_economics?.cost_per_message || 0).toFixed(4)}
              </p>
            </div>
            <div className="border-2 border-green-100 bg-green-50 rounded-lg p-4 hover:border-green-300 transition-colors">
              <p className="text-xs font-medium text-green-700 mb-1 flex items-center gap-1">
                💬 Cost per Conversation
                <span className="group relative">
                  <Info className="w-3 h-3 cursor-help" />
                  <span className="absolute left-0 top-5 w-48 bg-gray-900 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    Average cost for complete conversation thread
                  </span>
                </span>
              </p>
              <p className="text-2xl font-bold text-gray-900">
                ${parseFloat(overview?.unit_economics?.cost_per_conversation || 0).toFixed(2)}
              </p>
            </div>
            <div className="border-2 border-purple-100 bg-purple-50 rounded-lg p-4 hover:border-purple-300 transition-colors">
              <p className="text-xs font-medium text-purple-700 mb-1 flex items-center gap-1">
                🎯 Cost per Qualified Lead
                <span className="group relative">
                  <Info className="w-3 h-3 cursor-help" />
                  <span className="absolute left-0 top-5 w-48 bg-gray-900 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    Cost to acquire each qualified lead (Hot)
                  </span>
                </span>
              </p>
              <p className="text-2xl font-bold text-gray-900">
                ${parseFloat(overview?.unit_economics?.cost_per_qualified_lead || 0).toFixed(2)}
              </p>
            </div>
            <div className="border-2 border-orange-100 bg-orange-50 rounded-lg p-4 hover:border-orange-300 transition-colors">
              <p className="text-xs font-medium text-orange-700 mb-1 flex items-center gap-1">
                ⚡ Messages per Dollar
                <span className="group relative">
                  <Info className="w-3 h-3 cursor-help" />
                  <span className="absolute left-0 top-5 w-48 bg-gray-900 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    How many messages you get for every dollar spent
                  </span>
                </span>
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {parseFloat(overview?.unit_economics?.messages_per_dollar || 0).toFixed(1)}
              </p>
            </div>
          </div>
        </div>

        {/* Top Spending Customers */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">👥 Top 10 Spending Customers</h3>
              <p className="text-sm text-gray-500 mt-1">Highest cost tenants in selected period</p>
            </div>
            <Link to="/admin/financials/customers" className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
              View All
              <Users className="w-4 h-4" />
            </Link>
          </div>
          {overview?.top_spending_customers && overview.top_spending_customers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cost</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {overview.top_spending_customers.map((customer, idx) => (
                    <tr key={idx} className="hover:bg-blue-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-bold text-gray-900">
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${
                          idx === 0 ? 'bg-yellow-100 text-yellow-800' :
                          idx === 1 ? 'bg-gray-200 text-gray-700' :
                          idx === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {idx + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">
                          {customer.customer_name || customer.customer_email || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {customer.tenant_id}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <span className="font-bold text-gray-900">
                          ${parseFloat(customer.total_cost || 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <Link
                          to={`/admin/financials/customers/${customer.tenant_id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View Details →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-lg font-medium text-gray-700 mb-2">No customer data yet</p>
              <p className="text-sm text-gray-500">Cost data will appear here after SMS or AI calls are made</p>
              <p className="text-xs text-gray-400 mt-2">Data is tracked in the <code className="bg-gray-200 px-1 rounded">cost_events</code> table</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Link
            to="/admin/financials/explorer"
            className="flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all hover:shadow-lg"
          >
            <BarChart3 className="w-5 h-5" />
            Explore Costs
          </Link>
          <Link
            to="/admin/financials/invoices"
            className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all hover:border-blue-400"
          >
            <DollarSign className="w-5 h-5" />
            Manage Invoices
          </Link>
          <Link
            to="/admin/financials/unit-economics"
            className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all hover:border-blue-400"
          >
            <TrendingUp className="w-5 h-5" />
            Unit Economics
          </Link>
          <Link
            to="/admin/financials/rates"
            className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all hover:border-blue-400"
          >
            <Zap className="w-5 h-5" />
            Rate Management
          </Link>
        </div>
      </div>
      )}
    </div>
  );
}
